import { apiService } from '../ApiService';
import { databaseService } from '../offline/DatabaseService';
import { InspectionSaveRequest } from '@/types/request/InspectionRequests';
import { AttachmentUpload, FileAttachment, InspectionFormData, InspectionModalData } from '@/types/model/inspection-modal';
import { InspectionGroup, Inspection, ChecklistItem, TimeSensitivityStatus, InspectionResultStatus, InspectionId } from '@/types/model/inspection';
import { inspectionOrchestrationService } from "@/services/orchestration/InspectionOrchestrationService";
import { auth } from '../UserAuthService';
import databaseCachingService from '../offline/DatabaseCachingService';
import { InspectionLineItemResponse, InspectionResponse } from '@/types/response/InspectionResultResponses';
import _ from 'lodash'
import { buildAttachmentOperations } from '@/services/util/attachmentOperations';

class InspectionDataService {
  
  private async uploadAttachmentsSequentially(
    items: Array<{ id: number; attachments: FileAttachment[] }>,
    type: 'checklist' | 'corrective-action' | 'inspection'
  ): Promise<Map<number, AttachmentUpload[]>> {
    const uploadsMap = new Map<number, AttachmentUpload[]>();
    
    for (const item of items) {
      const uploads: AttachmentUpload[] = [];
      
      for (const attachment of item.attachments) {
        if (!attachment.fileId && attachment.file) {
          try {
            console.log(`Uploading ${type} attachment for item ${item.id}:`, attachment.fileName);
            const uploadResult = await apiService.uploadFile(attachment);
            console.log('Upload successful:', uploadResult);
            uploads.push({ attachment, uploadResult });
          } catch (error) {
            console.error(`Failed to upload ${type} attachment:`, error);
            throw new Error(`Failed to upload attachment: ${attachment.fileName}`);
          }
        }
      }
      
      if (uploads.length > 0) {
        uploadsMap.set(item.id, uploads);
      }
    }
    
    return uploadsMap;
  }

  async saveInspection(
    inspectionData: InspectionModalData, 
    inspectionFormData: InspectionFormData, 
    originalFormData: InspectionFormData
  ): Promise<void> {

    try {
      const checklistUploadsMap = await this.uploadAttachmentsSequentially(
        inspectionFormData.checklistItems.filter(item => item.attachments.length > 0),
        'checklist'
      );

      const inspectionAttachments = [{
        id: inspectionData.inspectionResultId,
        attachments: inspectionFormData.attachments,
      }];

      const inspectionUploadsMap = await this.uploadAttachmentsSequentially(
        inspectionAttachments.filter(item => item.attachments.length > 0),
        'inspection'
      );

      const getOperations = (
        attachments: FileAttachment[],
        originalAttachments: FileAttachment[],
        uploads: AttachmentUpload[],
      ) => buildAttachmentOperations({
        attachments,
        originalAttachments,
        uploadedFileIds: new Map(uploads.map(upload => [upload.attachment, upload.uploadResult.FileId])),
      });

      // Now create the inspection request with uploaded file IDs
      const inspectionRequest: InspectionSaveRequest = {
        Id: inspectionData.inspectionResultId,
        Comments: inspectionFormData.comments,
        LineItemId: inspectionData.lineItemId,
        SiteId: inspectionData.siteId,
        UserId: inspectionData.userId,
        ChecklistItems: inspectionFormData.checklistItems.map(item => ({
          Id: item.id,
          ChecklistItemId: item.checklistItemId,
          Score: item.score ?? null,
          Comment: item.comment || null,
          Attachments: getOperations(
            item.attachments,
            originalFormData.checklistItems.find(original => original.id === item.id)?.attachments ?? [],
            checklistUploadsMap.get(item.id) ?? [],
          ),
        })),
        IsExceptional: inspectionFormData.isExceptional,
        InspectedOn: new Date().toISOString(),
        Attachments: getOperations(
          inspectionFormData.attachments,
          originalFormData.attachments,
          inspectionUploadsMap.get(inspectionData.inspectionResultId) ?? [],
        ),
        AlertDays: inspectionData.alertDays ?? 0,
        AlertEmails: inspectionData.alertEmails ?? '',
        IsInProgress: inspectionData.isInProgress ?? true,
      };

      console.log('Saving Inspection request:', inspectionRequest);
      await apiService.saveInspection(inspectionRequest);
      
      console.log('✅ Inspection saved successfully');
    } catch (error) {
      console.error('❌ Failed to save inspection:', error);
      throw error;
    }
  }

  async createInspection(
    inspectionData: InspectionModalData, 
    inspectionFormData: InspectionFormData
  ): Promise<void> {
    const originalFormData: InspectionFormData = {
      comments: inspectionData.comments ?? '',
      isExceptional: false,
      checklistItems: inspectionFormData.checklistItems.map(item => ({
        id: item.id,
        score: null,
        comment: '',
        attachments: [],
      })),
      attachments: [],
    };

    await this.saveInspection(
      inspectionData,
      inspectionFormData,
      originalFormData,
    );
  }
  
  async updateInspectionCache(
    inspectionData: InspectionModalData,
    form: InspectionFormData): Promise<void> {

    console.log('Inspection cached locally with values:', {
      inspectionData,
      form
    });

      try {   
        const inspection: Inspection = {
          inspectionResultId: inspectionData.inspectionResultId,
          lineItemId: inspectionData.lineItemId,
          lineItem: inspectionData.lineItem, // Line item title can be added if needed
          title: inspectionData.title, // Inspection title can be added if needed
          siteName: inspectionData.siteName, // Site name can be added if needed
          siteId: inspectionData.siteId,
          inspectedOn: new Date().toISOString(),
          areaOfExpertise: '', // Area of expertise can be added if needed
          areaOfExpertiseId: 0, // Area of expertise ID can be added if needed
          userId: _.toNumber(auth.getUserDetails()?.userId) || inspectionData.userId || 0,
          inspectorName: inspectionData.inspectorName, // Inspector name can be added if needed
          compositeScore: null, // Composite score can be calculated if needed
          isExceptional: form.isExceptional,
          timeSensitivity: inspectionData.timeSensitivity, // Time sensitivity can be calculated if needed
          status: inspectionData.status, // Status can be set based on form data if needed
          checklistItems: form.checklistItems.map(item => {
            const checklistItem = inspectionData.checklistItems.find(ci => ci.Id === item.id);
            return {
            Id: item.id,
            Order: checklistItem?.Order ?? 0,
            Text: checklistItem?.Text ?? '',
            Weight: checklistItem?.Weight ?? 0,
            Score: item.score,
            Comment: item.comment || null,
            ResultsAnalysis: null,
            AttachmentCount: item.attachments.length,
            Attachments: item.attachments.map(att => ({
              Id: att.attachmentId || 0,
              Description: att.description || '',
              FileId: att.fileId ? Number(att.fileId) : 0,
              Filename: att.fileName || '',
              Link: att.link,
            }))
          };
          }),
          correctiveActions: null,
          inspectionPlanBackground: {
            LineItemPrimaryWeight: null,
            LineItemAlternateWeight: null,
            TimeToInspect: null,
            MethodOfInspection: null,
            SampleSize: null,
            SampleSelection: null,
            AdditionalInstructions: null,
            InspectionPlanAttachments: [],
          },
          lineItemDetail: {
            MajorRequirement: null,
            RelatedInfo: null,
            EstimatedQuantity: null,
            PerformanceStandard: null,
            LineItemAttachments: [],
          },
        };

        await databaseCachingService.updateCachedInspection(inspection);
      } catch (error) {
        console.error('❌ Failed to cache inspection:', error);
        throw error;
      }

  }
    
  public async getGroupedInspectionData(groupIds: number[] = [], siteIds: number[] = [], areaIds: number[] = [], from?: string, to?: string): Promise<InspectionGroup[]> {
    return await inspectionOrchestrationService.getGroupedInspectionData(groupIds, siteIds, areaIds, from, to);
  }

  public async getInspections(ids: number[]): Promise<Inspection[]> {
    return await inspectionOrchestrationService.getInspections(ids);
  }

  public async getContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    return await apiService.getContractInspection(inspectionResultId);
  }

  public async getCachedContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    const cachedInspections = await this.getCachedInspections([inspectionResultId]);
    const cachedInspection = cachedInspections[0];

    if (!cachedInspection) {
      throw new Error(`Cached inspection ${inspectionResultId} not found`);
    }

    return this.synthesizeCachedContractInspection(cachedInspection);
  }

  private synthesizeCachedContractInspection(cachedInspection: Inspection): InspectionLineItemResponse {
    const inspectionResult: InspectionResponse = {
      Id: cachedInspection.inspectionResultId,
      InspectedOn: cachedInspection.inspectedOn,
      CreateDate: cachedInspection.createDate || cachedInspection.inspectedOn || '',
      CreateUserId: cachedInspection.userId || 0,
      CompositeScore: cachedInspection.compositeScore,
      FrequencyId: 0,
      LevelId: 0,
      Comments: '',
      ChecklistItems: cachedInspection.checklistItems,
      IsExceptional: cachedInspection.isExceptional,
      SiteId: cachedInspection.siteId,
      Attachments: null,
      UserId: cachedInspection.userId || null,
      UserTypeId: null,
      UpdateUserId: null,
      UpdatedDate: null,
      IsApproved: cachedInspection.status === 'approved' ? true : cachedInspection.status === 'disapproved' ? false : null,
      ApproveUserId: null,
      ApproveDate: null,
      ApprovalComments: null,
      AlertDays: 0,
      AlertEmails: '',
      IsInProgress: cachedInspection.status === 'in-progress',
      ApprovalStatus: cachedInspection.status === 'approved'
        ? 'Approved'
        : cachedInspection.status === 'disapproved'
          ? 'Disapproved'
          : null,
    };

    return {
      Id: cachedInspection.lineItemId,
      LineItem: cachedInspection.lineItem,
      Title: cachedInspection.title,
      SiteId: cachedInspection.siteId,
      InspectionMethodId: 0,
      ExpertiseAreaId: cachedInspection.areaOfExpertiseId,
      InspectionResults: [inspectionResult],
      ChecklistItems: cachedInspection.checklistItems,
      MajorRequirement: undefined,
      RelatedInfo: null,
      EstimatedQuantity: null,
      PerformanceStandard: null,
      PrimaryValue: null,
      AlternateValue: null,
      TimeToInspect: null,
      SampleSize: null,
      SampleSelection: null,
      AdditionalInstructions: null,
      AutoInspections: null,
      FixedInspections: null,
      LineItemAttachments: [],
      InspectionPlanAttachments: [],
      Attachments: [],
    };
  }

  public async processCachedInspection(cachedInspection: any): Promise<Inspection | null> {
    try {
        // Get checklist items for this inspection
        const checklistItems = await databaseService.select(
          'cached_checklist_items', 
          'inspection_result_id = ?', 
          [cachedInspection.inspection_result_id]
        );

        // Process checklist items and their attachments
        const processedChecklistItems: ChecklistItem[] = [];
        for (const item of checklistItems) {
          const attachments = await databaseService.select(
            'cached_attachments',
            'parent_type = ? AND parent_id = ?',
            ['checklist_item', item.id]
          );

          processedChecklistItems.push({
            Id: item.checklist_item_id,
            Order: item.order_index || 0,
            Text: item.text || '',
            Weight: item.weight || 1,
            Score: item.score,
            Comment: item.comment,
            ResultsAnalysis: item.resultsAnalysis,
            AttachmentCount: attachments.length,
            Attachments: attachments.map(att => ({
              Id: att.id,
              Description: att.description || '',
              FileId: att.file_id || 0,
              Filename: att.file_name || '',
              Link: att.link
            }))
          });
        }

        // Create the inspection object
        const inspection: Inspection = {
          inspectionResultId: cachedInspection.inspection_result_id,
          lineItemId: cachedInspection.line_item_id || 0,
          lineItem: cachedInspection.line_item || '',
          title: cachedInspection.line_item_title || '',
          siteName: cachedInspection.site_name || '',
          siteId: cachedInspection.site_id || 0,
          isExceptional: cachedInspection.is_exceptional ?? false,
          inspectedOn: cachedInspection.inspected_on || '',
          areaOfExpertise: cachedInspection.area_of_expertise || '',
          areaOfExpertiseId: cachedInspection.area_of_expertise_id || 0,
          userId: cachedInspection.user_id || 0,
          inspectorName: cachedInspection.inspector_name || '',
          compositeScore: cachedInspection.composite_score,
          timeSensitivity: (cachedInspection.time_sensitivity as TimeSensitivityStatus) || 'unknown',
          status: (cachedInspection.status as InspectionResultStatus) || 'unknown',
          checklistItems: processedChecklistItems,
          correctiveActions: null,
          inspectionPlanBackground: {
            LineItemPrimaryWeight: cachedInspection.line_item_primary_weight,
            LineItemAlternateWeight: cachedInspection.line_item_alternate_weight,
            TimeToInspect: cachedInspection.time_to_inspect,
            MethodOfInspection: cachedInspection.method_of_inspection,
            SampleSize: cachedInspection.sample_size,
            SampleSelection: cachedInspection.sample_selection,
            AdditionalInstructions: cachedInspection.additional_instructions,
            InspectionPlanAttachments: cachedInspection.inspection_plan_attachments || []
          },
          lineItemDetail: {
            MajorRequirement: cachedInspection.major_requirement,
            RelatedInfo: cachedInspection.related_info,
            EstimatedQuantity: cachedInspection.estimated_quantity,
            PerformanceStandard: cachedInspection.performance_standard,
            LineItemAttachments: cachedInspection.line_item_attachments || [],
          },
        };

        return inspection;

    } catch (error) {
      console.error('Error fetching cached inspections:', error);
      return null;
    }
  }
  
  public async getCachedInspections(inspectionResultIds: number[]): Promise<Inspection[]> {
    const inspections: Inspection[] = [];
    try {
      console.log('Fetching cached inspections for ID:', inspectionResultIds);
      const cachedInspections = await databaseService.select(
        'cached_inspections',
        `inspection_result_id IN (${inspectionResultIds.map(() => '?').join(',')})`,
        inspectionResultIds
      );

      console.log('Cached inspections retrieved:', cachedInspections);
      for (const cachedInspection of cachedInspections) {
          const inspection = await this.processCachedInspection(cachedInspection);
          if (inspection) {
              inspections.push(inspection);
          }
      }
      return inspections;
    } catch (error) {
      console.error('Error fetching cached inspections:', error);
      return [];
    }
  }

  public async getCachedContractInspectionGroups(): Promise<InspectionGroup[]> {
    try {
      console.log('🔄 Fetching grouped inspection data from cache...');
      
      const contractId = auth.getCurrentContractId();

      // Get all cached inspections
      const cachedInspections = await databaseService.select(
        'cached_inspections',
          'contract_id = ?',
          [contractId]
      );
      
      if (cachedInspections.length === 0) {
        console.log('No cached inspections found');
        return [];
      }

      console.log(`Found ${cachedInspections.length} cached inspections, processing...`);

      // Group inspections by group_id and group_identifier
      const groupsMap = new Map<string, {
        id: number;
        title: string;
        groupIdentifier: number;
        lineItemsMap: Map<number, {
          id: number;
          lineItem: string;
          title: string;
          groupId: number;
          subItems: InspectionId[];
          inspections: Inspection[];
        }>;
      }>();

      // Process each cached inspection
      for (const cachedInspection of cachedInspections) {
        const groupKey = `${cachedInspection.group_id}_${cachedInspection.group_identifier}`;
        
        if (!groupsMap.has(groupKey)) {
          groupsMap.set(groupKey, {
            id: cachedInspection.group_id,
            title: cachedInspection.group_title || 'Untitled Group',
            groupIdentifier: cachedInspection.group_identifier,
            lineItemsMap: new Map()
          });
        }

        const group = groupsMap.get(groupKey)!;
        const lineItemId: number = cachedInspection.line_item_id || 0;
        if (!group.lineItemsMap.has(lineItemId)) {
          group.lineItemsMap.set(lineItemId, {
            id: lineItemId,
            lineItem: cachedInspection.line_item || '',
            title: cachedInspection.line_item_title || '',
            groupId: group.id,
            subItems: [],
            inspections: []
          });
        }
        const lineItem = group.lineItemsMap.get(lineItemId)!;
        lineItem.subItems.push({ id: cachedInspection.inspection_result_id, status: cachedInspection.status, timeSensitivity: cachedInspection.time_sensitivity });

        const inspection: Inspection | null = await this.processCachedInspection(cachedInspection);
        if (inspection) {
          lineItem.inspections.push(inspection);
        }
      }

      console.log(`✅ Processed ${groupsMap.size} inspection groups from cache`);

      // Convert map to array and return as InspectionGroup[]
      const result: InspectionGroup[] = Array.from(groupsMap.values()).map(group => ({
        id: group.id,
        title: group.title,
        groupIdentifier: group.groupIdentifier,
        lineItems: Array.from(group.lineItemsMap.values()).map(li => ({
          id: li.id,
          lineItem: li.lineItem,
          title: li.title,
          groupId: li.groupId,
          subItems: li.subItems,
          inspections: li.inspections
        }))
      }));

      console.log(`✅ Retrieved ${result.length} inspection groups from cache`);
      return result;

    } catch (error) {
      console.error('❌ Failed to fetch cached grouped inspection data:', error);
      return [];
    }
  }
}

export const inspectionDataService = new InspectionDataService();
export default inspectionDataService;